'use client'

import { useCallback, useEffect, useRef, useState, Children } from 'react'

/**
 * Pozioma karuzela bestsellerów (wzór x-kom): przewijalna scroll-snap lista
 * z kafelkami stałej szerokości + strzałki prev/next. Kafelki przychodzą jako
 * children (renderowane po stronie serwera w page.tsx). Na dotyku działa swipe;
 * na desktopie strzałki + scroll-snap. Strzałki chowają się na krańcach.
 */
export function ProductCarousel({ children }: { children: React.ReactNode }) {
  const scroller = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)
  const items = Children.toArray(children)

  const update = useCallback(() => {
    const el = scroller.current
    if (!el) return
    setAtStart(el.scrollLeft <= 4)
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    update()
    const el = scroller.current
    if (!el) return
    el.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      el.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [update])

  const scrollByPage = (dir: 1 | -1) => {
    const el = scroller.current
    if (!el) return
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' })
  }

  return (
    <div className="relative">
      <div ref={scroller} className="carousel-track flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mb-2">
        {items.map((child, i) => (
          <div key={i} className="snap-start shrink-0 w-[78%] sm:w-[300px]">
            {child}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => scrollByPage(-1)}
        aria-label="Poprzednie produkty"
        className="carousel-arrow carousel-arrow-prev"
        style={{ opacity: atStart ? 0 : 1, pointerEvents: atStart ? 'none' : 'auto' }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => scrollByPage(1)}
        aria-label="Następne produkty"
        className="carousel-arrow carousel-arrow-next"
        style={{ opacity: atEnd ? 0 : 1, pointerEvents: atEnd ? 'none' : 'auto' }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  )
}
